import { Button, Modal, ThemeProvider } from '@alphonsocode/kallos';
import React, { useCallback, useEffect, useState } from 'react';
import { ModalStyle } from './style';
import useQuery from 'reusable/customHooks/useQuery';
import ModalBody from './modalBody';
import Spinners from '../Spinner';
import { nanoid } from '@reduxjs/toolkit';
import { track } from 'analytics/posthog';

interface Filter {
  filter: { label: string; key?: string; value?: string } | null; // Ensure `filter` is an object with key and value
  value: { label: string; value: string }[] | null;
}
function ModalComponent({ filters, isFetching }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useQuery();
  const [setupFlag, setSetupFlag] = useState(false);

  const [rows, setRows] = useState([nanoid()]);
  const [filtersState, setFiltersState] = useState<Filter[]>([
    { filter: null, value: null },
  ]);

  const mapQueryToFiltersState = useCallback(() => {
    if (isFetching) return;
    const keys = Array.from(query.keys()).filter(
      key =>
        key !== 'testTag' &&
        key !== 'testId' &&
        filters.some(f => f.key === key),
    );

    const newFiltersState: Filter[] = keys.map(key => {
      const matchingFilter = filters.find(f => f.key === key);
      const valueString = query.get(key);

      const values = valueString
        ? valueString.split(',').map(val => ({ label: val, value: val }))
        : null;
      return {
        filter: matchingFilter || null,
        value: values,
      };
    });

    setFiltersState(
      newFiltersState.length
        ? newFiltersState
        : [{ filter: null, value: null }],
    );
    const newRows =
      newFiltersState.length > 0
        ? newFiltersState.map(() => nanoid())
        : [nanoid()];

    setRows(newRows);
    setSetupFlag(true);
  }, [filters, isFetching, query]);

  const handleOpen = () => {
    setSetupFlag(false);
    track('adsure advanced filters opened', {
      existing_filter_count: filtersState.filter(row => row.filter && row.value).length,
      adsure_test_tag: query.get('testTag') || 'cta',
      surface: 'adsure',
    });
    setOpen(true);
  };
  useEffect(() => {
    if (open && !isFetching) {
      mapQueryToFiltersState();
    }
  }, [open, isFetching, mapQueryToFiltersState]);

  const handleSave = () => {
    const newQueryParams = {};
    // Clear previous filter keys from URL
    const queryKeys = Array.from(query.keys()).filter(key =>
      filters.some(f => f.key === key),
    );
    queryKeys.forEach(key => {
      if (key !== 'testTag') {
        newQueryParams[key] = '';
      }
    });
    // Add new filters
    filtersState.forEach(filterState => {
      if (filterState.filter && filterState.value) {
        const filterKey = filterState.filter.key
          ? filterState.filter.key
          : filterState.filter.value
            ? filterState.filter.value
            : '';
        const filterValue = filterState.value.map(val => val.value).join(',');
        if (filterKey) newQueryParams[filterKey] = filterValue;
      }
    });
    newQueryParams['page'] = '1';
    track('adsure filters applied', {
      adsure_test_tag: query.get('testTag') || 'cta',
      filter_count: filtersState.filter(row => row.filter && row.value?.length).length,
      filter_keys: filtersState
        .filter(row => row.filter && row.value?.length)
        .map(row => row.filter?.key || row.filter?.value),
      my_campaigns: query.get('myCampaigns') === 'true',
      my_tests: query.get('myTests') === 'true',
      surface: 'adsure',
    });
    setQuery(newQueryParams);
    setOpen(false);
  };

  return (
    <ModalStyle>
      <div className="modalDiv d-flex flex-column">
        <label className="heading">&nbsp;</label>
        {/* @ts-ignore - ThemeProvider children type issue with React 18 */}
        <ThemeProvider defaultMode="light">
          <Button
            className="filterButton"
            colorVariant="deepGreen"
            variant="rounded"
            onClick={handleOpen}
            disableHoverEffect={true}
            style={{
              whiteSpace: 'nowrap',
              width: 'auto',
              height: '35px',
              minHeight: '35px',
              marginRight: '15px',
              padding: '7px 14px',
              borderRadius: '5px',
              fontSize: '13px',
              lineHeight: '18px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
            }}
          >
            Advanced Filters
          </Button>
          <Modal.Root open={open}>
            <Modal.Overlay />
            <Modal.Dialog size={'xl'}>
              <Modal.Header closeButton={true} onClick={() => setOpen(false)}>
                <Modal.Title style={{ fontSize: '22px' }}>
                  Advanced Filters
                </Modal.Title>
              </Modal.Header>
              {!setupFlag ? (
                <Spinners count={4} color={'#157f95'} />
              ) : (
                <ModalBody
                  rows={rows}
                  setRows={setRows}
                  filtersState={filtersState}
                  setFiltersState={setFiltersState}
                  filters={filters}
                  isFetchingFilters={isFetching}
                />
              )}
              <Modal.Footer>
                <div className="col-6"></div>
                <div className="d-flex justify-content-end gap-2 col-6 footer">
                  <div className="col-3">
                    <Button
                      onClick={() => setOpen(false)}
                      colorVariant="deepGreen"
                      variant="outlined"
                      style={{ width: '100%', fontSize: '14px' }}
                    >
                      Close
                    </Button>
                  </div>
                  <div className="col-3 footerButton">
                    <Button
                      onClick={handleSave}
                      colorVariant="deepGreen"
                      variant="rounded"
                      style={{ width: '100%', fontSize: '14px' }}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </Modal.Footer>
            </Modal.Dialog>
          </Modal.Root>
        </ThemeProvider>
      </div>
    </ModalStyle>
  );
}

export default ModalComponent;
